export interface GenerationSocketOptions {
  url: string
  payload: string
  onMessage: (data: any) => void
  onError: (error: Event | Error) => void
  onClose: (event: CloseEvent) => void
  onOpen?: () => void
}

const HEARTBEAT_INTERVAL = 30_000
const CONNECTION_TIMEOUT = 5_000
const HEARTBEAT_PING = JSON.stringify({ type: 'ping' })

/** 管理单一生成会话的连接替换、心跳、超时和消息解析。 */
export function useGenerationSocket() {
  let socket: WebSocket | null = null
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null
  let connectionTimer: ReturnType<typeof setTimeout> | null = null
  let connectionVersion = 0

  function clearTimers() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer)
      heartbeatTimer = null
    }
    if (connectionTimer) {
      clearTimeout(connectionTimer)
      connectionTimer = null
    }
  }

  function closeActiveSocket() {
    clearTimers()
    const activeSocket = socket
    socket = null
    if (!activeSocket) {
      return
    }
    activeSocket.onopen = null
    activeSocket.onmessage = null
    activeSocket.onerror = null
    activeSocket.onclose = null
    if (activeSocket.readyState === WebSocket.CONNECTING || activeSocket.readyState === WebSocket.OPEN) {
      activeSocket.close()
    }
  }

  function close() {
    connectionVersion += 1
    closeActiveSocket()
  }

  function startHeartbeat(activeSocket: WebSocket, onError: (error: Error) => void) {
    heartbeatTimer = setInterval(() => {
      if (activeSocket.readyState === WebSocket.OPEN) {
        try {
          activeSocket.send(HEARTBEAT_PING)
        }
        catch (error) {
          closeActiveSocket()
          onError(error as Error)
        }
      }
    }, HEARTBEAT_INTERVAL)
  }

  async function connect(options: GenerationSocketOptions) {
    const version = ++connectionVersion
    closeActiveSocket()
    // 给浏览器一个任务间隔完成旧连接关闭，避免快速连续生成时连接交叉。
    await new Promise(resolve => setTimeout(resolve, 100))
    if (version !== connectionVersion) {
      return
    }

    const activeSocket = new WebSocket(options.url)
    socket = activeSocket

    activeSocket.onopen = () => {
      if (socket !== activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
        return
      }
      if (connectionTimer) {
        clearTimeout(connectionTimer)
        connectionTimer = null
      }
      activeSocket.send(options.payload)
      startHeartbeat(activeSocket, options.onError)
      options.onOpen?.()
    }

    activeSocket.onmessage = (event) => {
      if (socket !== activeSocket || typeof event.data !== 'string') {
        return
      }
      const raw = event.data.trim()
      if (raw === 'pong' || raw === 'ping') {
        return
      }
      try {
        const data = JSON.parse(raw)
        if (data?.type !== 'pong' && data?.type !== 'ping') {
          options.onMessage(data)
        }
      }
      catch (error) {
        options.onError(error as Error)
      }
    }

    activeSocket.onerror = (event) => {
      if (socket !== activeSocket) {
        return
      }
      clearTimers()
      socket = null
      options.onError(event)
    }

    activeSocket.onclose = (event) => {
      if (socket !== activeSocket) {
        return
      }
      clearTimers()
      socket = null
      options.onClose(event)
    }

    connectionTimer = setTimeout(() => {
      if (socket === activeSocket && activeSocket.readyState === WebSocket.CONNECTING) {
        closeActiveSocket()
        options.onError(new Error('WebSocket connection timeout'))
      }
    }, CONNECTION_TIMEOUT)
  }

  return {
    close,
    connect,
    isConnected: () => socket?.readyState === WebSocket.OPEN,
  }
}

/** 应用级单例：路由组件停用或卸载不会终止正在进行的生成连接。 */
export const generationSocket = useGenerationSocket()
